/**
 * FeeManagement 헬퍼 함수
 * 수수료 관리 관련 유틸리티
 */

import { SupplierCommissionRow, SupplierStatus } from '../types/feeManagement.types';

// 공통 상수 re-export (shared/constants에서 관리)
export { COMMISSION_TYPE_OPTIONS, getAmountUnit } from '../../shared/constants';

// 필터 기본값
const DEFAULT_FILTER = {
    ALL: '전체',
    ALL_SUPPLIER_RIDERS: '전협력사 라이더'
} as const;

/** 협력사 상태 필터 옵션 */
export const STATUS_FILTER_OPTIONS = [
    { value: '', label: '전체' },
    { value: 'OPERATING', label: '운영중' },
    { value: 'READY', label: '준비중' },
    { value: 'SUSPENDED', label: '운영중지' },
    { value: 'TERMINATION_REQUESTED', label: '계약종료 접수' },
    { value: 'TERMINATED', label: '계약종료' },
] as const;

// ======== 상수 ========

/** 스토리지 키 */
export const FILTER_STORAGE_KEY = 'feeManagementFilters';

/** 페이지당 항목 수 */
export const ITEMS_PER_PAGE = 10;

/** 테이블 컬럼 수 (No, 협력사명, 선정산, 주정산, 페이백(건당), 적용일, 관리) */
export const TABLE_COLUMN_COUNT = 7;

/**
 * 기본 수수료 초기값
 * 주의: 이 값들은 vr_comm_code에서 동적으로 가져와야 함
 * getFeeDefaultValues() 함수를 사용하여 실제 값을 조회하세요
 */
export const DEFAULT_COMMISSION_VALUES = {
    dailyCommissionType: 'PER_CASE_FIXED',
    dailyAmount: '0',  // vr_comm_code에서 조회
    weeklyCommissionType: 'PER_CASE_FIXED',
    weeklyAmount: '0'  // vr_comm_code에서 조회
} as const;

// ======== 헬퍼 함수 ========

/**
 * 오늘 날짜 문자열 반환 (YYYY-MM-DD)
 */
export const getTodayString = (): string => {
    return new Date().toISOString().split('T')[0];
};

/**
 * 협력사명으로 데이터 필터링
 */
export const filterByPartner = (
    data: SupplierCommissionRow[],
    partnerId: string
): SupplierCommissionRow[] => {
    if (!partnerId || partnerId === DEFAULT_FILTER.ALL) return data;
    return data.filter(item => item.supplierName === partnerId);
};

/**
 * 협력사 상태로 데이터 필터링
 */
export const filterByStatus = (
    data: SupplierCommissionRow[],
    status: string
): SupplierCommissionRow[] => {
    if (!status) return data;
    return data.filter(item => item.status === status);
};

/**
 * 협력사명과 상태로 데이터 필터링
 */
export const filterCommissionData = (
    data: SupplierCommissionRow[],
    partnerId: string,
    status: string
): SupplierCommissionRow[] => {
    let filtered = data;

    if (partnerId && partnerId !== DEFAULT_FILTER.ALL) {
        filtered = filtered.filter(item => item.supplierName === partnerId);
    }

    if (status) {
        filtered = filtered.filter(item => item.status === status);
    }

    return filtered;
};

/**
 * API 응답을 테이블 행 데이터로 변환
 * 각 협력사에 실제 적용된 수수료 정보를 표시
 */
export const transformCommissionData = (
    commissions: Array<{
        supplierId: string;
        supplierName: string;
        status?: string;
        dailyCommissionType?: string;
        dailyAmount?: number;
        weeklyCommissionType?: string;
        weeklyAmount?: number;
        weeklyPaybackUseYn?: string;
        weeklyPaybackAmount?: number;
        scheduledPaybackUseYn?: string;
        scheduledPaybackAmount?: number;
        scheduledPaybackApplyDate?: string;
        startDate?: string;
        endDate?: string;
    }>
): SupplierCommissionRow[] => {
    return commissions.map(comm => ({
        supplierId: comm.supplierId,
        supplierName: comm.supplierName,
        status: (comm.status as 'OPERATING' | 'READY' | 'SUSPENDED' | 'TERMINATION_REQUESTED' | 'TERMINATED') || 'OPERATING',
        dailyCommissionType: comm.dailyCommissionType || '',
        dailyAmount: comm.dailyAmount ? comm.dailyAmount.toString() : '',
        weeklyCommissionType: comm.weeklyCommissionType || '',
        weeklyAmount: comm.weeklyAmount ? comm.weeklyAmount.toString() : '',
        weeklyPaybackUseYn: comm.weeklyPaybackUseYn || 'N',
        weeklyPaybackAmount: comm.weeklyPaybackAmount ? comm.weeklyPaybackAmount.toString() : '',
        scheduledPaybackUseYn: comm.scheduledPaybackUseYn || '',
        scheduledPaybackAmount: comm.scheduledPaybackAmount ? comm.scheduledPaybackAmount.toString() : '',
        scheduledPaybackApplyDate: comm.scheduledPaybackApplyDate || '',
        startDate: comm.startDate || '',
        endDate: comm.endDate || '',
        isEdited: false
    }));
};

/**
 * 모든 협력사에 기본값 설정
 * 운영중(OPERATING) 상태인 협력사만 초기값 설정
 * new* 필드에 값을 설정하고 isAddingNew를 true로 설정 (수수료변경 모드 활성화)
 * @param data 협력사 수수료 데이터
 * @param dailyAmount 일정산 기본 금액 (vr_comm_code에서 조회된 값)
 * @param weeklyAmount 주정산 기본 금액 (vr_comm_code에서 조회된 값)
 */
export const applyDefaultValuesToAll = (
    data: SupplierCommissionRow[],
    dailyAmount: string = DEFAULT_COMMISSION_VALUES.dailyAmount,
    weeklyAmount: string = DEFAULT_COMMISSION_VALUES.weeklyAmount
): SupplierCommissionRow[] => {
    const today = getTodayString();
    return data.map(row => {
        // 운영중(OPERATING)이 아닌 상태는 초기값 설정 제외
        // (운영중지, 계약종료 접수, 계약종료)
        if (row.status !== 'OPERATING') {
            return row;
        }

        return {
            ...row,
            // new* 필드에 기본값 설정 (수수료변경 행에 표시)
            newDailyCommissionType: DEFAULT_COMMISSION_VALUES.dailyCommissionType,
            newDailyAmount: dailyAmount,
            newWeeklyCommissionType: DEFAULT_COMMISSION_VALUES.weeklyCommissionType,
            newWeeklyAmount: weeklyAmount,
            newStartDate: today,
            // 수수료변경 모드 활성화
            isAddingNew: true,
            isEdited: true
        }
    });
};

/**
 * 협력사 목록에서 고유한 협력사명 추출 (정렬됨)
 */
export const getUniqueSupplierNames = (data: SupplierCommissionRow[]): string[] => {
    return Array.from(new Set(data.map(item => item.supplierName))).sort();
};

/**
 * 협력사 상태별 정렬 우선순위
 * OPERATING(운영중) -> READY(준비중) -> SUSPENDED(운영중지) -> TERMINATION_REQUESTED(계약종료 접수) -> TERMINATED(계약종료)
 */
const STATUS_ORDER = {
    'OPERATING': 1,
    'READY': 2,
    'SUSPENDED': 3,
    'TERMINATION_REQUESTED': 4,
    'TERMINATED': 5
} as const;

/**
 * 협력사 상태별로 정렬
 */
export const sortByStatus = (data: SupplierCommissionRow[]): SupplierCommissionRow[] => {
    return [...data].sort((a, b) => {
        const orderA = STATUS_ORDER[a.status] || 999;
        const orderB = STATUS_ORDER[b.status] || 999;
        return orderA - orderB;
    });
};
